import { AvatarProps } from '@fluentui/react-components';
import React, { FC, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getCourseColor, getCourseTextColor } from '../Utils';
import { AvatarContext } from './AvatarContext';
import { LazyAvatar } from './LazyAvatar';
import { ThemeMode, useProviderTheme } from '../hooks';

export interface LazyAvatarGroupProps extends AvatarProps {
  groupId: string;
  disablePictureLoading?: boolean;
}

/**
 * Renders the photo of the group given by the group id.
 * If no photo exists, the initials will be calculated by the value given as "name".
 *
 * Note:
 * This component requires an AvatarContext to pick an existing photo
 * or to dispatch a load picture function if no photo was loaded yet.
 *
 * @return {*}
 */
export const LazyAvatarGroup: FC<LazyAvatarGroupProps> = ({
  groupId,
  disablePictureLoading,
  size,
  ...rest
}) => {
  const { t } = useTranslation();
  const theme = useProviderTheme();
  const { byGroup, loadGroupPicture } = useContext(AvatarContext);

  const courseColor = useMemo(() => {
    return theme.mode === ThemeMode.HighContrast ? '#1aebff' : getCourseColor({ id: groupId });
  }, [groupId, theme.mode]);

  const textColor = useMemo(() => {
    return theme.mode === ThemeMode.HighContrast ? '#fff' : getCourseTextColor({ id: groupId });
  }, [groupId, theme.mode]);

  const picture = byGroup[groupId];
  const isPictureRequested = picture?.isRequested;
  const pictureUrl = picture?.url;

  const imageProps = useMemo(
    () => ({
      alt: t('translation:groupAvatar', { defaultValue: 'Gruppen Avatar' }),
      src: pictureUrl,
    }),
    [pictureUrl, t],
  );

  const handleLoad = useCallback(() => {
    if (disablePictureLoading) {
      return;
    }
    loadGroupPicture(groupId);
  }, [disablePictureLoading, loadGroupPicture, groupId]);

  return (
    <LazyAvatar
      {...rest}
      key={`lazy-group-avatar-${groupId}`}
      backgroundColor={courseColor}
      color={textColor}
      image={imageProps}
      isLoaded={isPictureRequested}
      size={size}
      shape="square"
      load={handleLoad}
    />
  );
};
